import React from "react";
import { theme, Tooltip, Modal, Badge, Button } from 'antd';
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  StopFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import ColorButton from "../../common/ColorButton";
import TSTable from "../../common/TSTable";
import { GRAY_COLOR, LAB_COLOR, PRIMARY_COLOR } from "../../constants/color";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlask, faTruck, faFillDrip } from "@fortawesome/free-solid-svg-icons";
import PondOpenLotModal from "./PondOpenLotModal";
import { URL_POND } from "../../constants/urls";
import { LAB_ORDER_TYPE, POND_STATUS, POND_STATUS_TH, } from "../../constants/string";
import _ from "lodash";
import PondTruckLogModal from "./PondTruckLogModal";
import PondCloseModal from "./PondCloseModal";
import { useNavigate } from "react-router-dom";
import { POST } from "../../frameworks/HttpClient";
import { PATH } from "../../routes/CustomRoutes";
import ErrorMessages from "../../common/ErrorMessages";
import PondWeightLogModal from "./PondWeightLogModal";
import PondStatusLogModal from "./PondStatusLogModal";
import PondWeightPopover from "./PondWeightPopover";
import { formatComma, toFixed } from "../../frameworks/Util";

const PondLatexList = React.forwardRef((props, ref) => {
  const [openLotTarget, setOpenLotTarget] = React.useState(null);
  const [truckLogTarget, setTruckLogTarget] = React.useState(null);
  const [closePondTarget, setClosePondTarget] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [targetWeightLog, setTargetWeightLog] = React.useState(null);
  const [targetStatusLog, setTargetStatusLog] = React.useState(null);
  const tableRef = React.useRef();
  const navigate = useNavigate();

  const refreshData = async() => {
    if(tableRef.current){
      if(props.fetchTruckCount){
        await props.fetchTruckCount();
      }
      await tableRef.current.fetchWithoutResetPage()
    }
  }

  const handleOpenPondProduce = async(id) => {
    try {
      const data = {
        action : 'produce'
      }
      await POST(`${URL_POND.POND_TRANSACTION}${id}/change-state/`, data)
      refreshData();
    } catch (error) {
      setErrorMessage(error.errorMessages)
    }
  }

  const handleEndWork = async(id) => {
    try {
      const data = {
        action : 'end'
      }
      await POST(`${URL_POND.POND_TRANSACTION}${id}/change-state/`, data)
      refreshData();
    } catch (error) {
      setErrorMessage(error.errorMessages)
    }
  }

  const confirm = (title, value) => {
    Modal.confirm({
      title: `เปิดบ่อผลิต - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'คุณต้องการเปิดบ่อเพื่อผลิตใช่หรือไม่ ?',
      okText: 'ยืนยัน',
      cancelText: 'ปิด',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : PRIMARY_COLOR}},
      onOk:() => {handleOpenPondProduce(value)},
      onConfirm: () => refreshData()
    });
  };

  const confirmEnd = (title, value) => {
    Modal.confirm({
      title: `จบการทำงาน - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'คุณต้องการจบการทำงานบ่อใช่หรือไม่ ?',
      okText: 'ยืนยัน',
      cancelText: 'ปิด',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : PRIMARY_COLOR}},
      onOk:() => {handleEndWork(value)},
      onConfirm: () => refreshData()
    });
  };


  React.useImperativeHandle(ref, () => ({
    fetch : () => refreshData()
  }));

  const {token : { colorWarning, colorSuccess }} = theme.useToken();

  const columns = [
    {
      title: 'Action',
      dataIndex: 'id',
      width : 200,
      key: 'action',
      render: (value, original) => {
        return (
          <div 
            style={{textAlign:'center'}}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            {!original.state && props.role !== 'tank' &&
            <Tooltip triger='hover' title={"เปิดบ่อ"}>
              <ColorButton 
                type='primary'
                style={{ marginLeft : 10}}
                override={PRIMARY_COLOR}
                icon={<PlusCircleOutlined />}
                onClick={()=> setOpenLotTarget(original)} 
              />
            </Tooltip>}

            {original.state === POND_STATUS.FILLING && props.role !== 'tank' &&
            <Tooltip triger='hover' title={"แก้ไข"}>
              <ColorButton 
                type='primary'
                style={{ marginLeft : 10}}
                override={colorWarning}
                icon={<EditOutlined />}
                onClick={()=> setOpenLotTarget(original)} 
              />
            </Tooltip>}

            {original.state === POND_STATUS.FILLING && props.role !== 'tank' &&
            <Tooltip triger='hover' title={"ประวัติเติมน้ำยาง"}>
              <ColorButton 
                type='primary'
                override={PRIMARY_COLOR}
                style={{ marginLeft : 10}}
                icon={<FontAwesomeIcon icon={faTruck} />}
                onClick={()=> setTruckLogTarget(original)} 
              />
            </Tooltip>}

            {(original.state === POND_STATUS.FILLING || original.state === POND_STATUS.CLOSED) && props.role !== 'tank' &&
            <Badge count={original.is_input_progressing ? '!' : 0} size={'small'}>
            <Tooltip triger='hover' title={"แล็บบ่อพักยาง"}>
              <ColorButton 
                type='primary'
                override={LAB_COLOR}
                style={{ marginLeft : 10}}
                icon={<FontAwesomeIcon icon={faFlask}/>}
                onClick={()=> navigate(`${PATH.POND_LAB}/${original.current_transaction}/${LAB_ORDER_TYPE.POND}/`)} 
              />
            </Tooltip>
            </Badge>}

            {original.state === POND_STATUS.FILLING && props.role !== 'tank' &&
            <Tooltip triger='hover' title={"ปิดบ่อ"}>
              <ColorButton 
                type='primary'
                style={{ marginLeft : 10}}
                override={colorWarning}
                icon={<MinusCircleOutlined />}
                onClick={()=> setClosePondTarget(original)} 
            /></Tooltip>}

            {original.state === POND_STATUS.CLOSED && props.role !== 'tank' &&
            <Tooltip triger='hover' title={"ผลิต"}>
              <ColorButton 
                type='primary'
                override={colorSuccess}
                style={{ marginLeft : 10}}
                icon={<FontAwesomeIcon icon={faFillDrip} />}
                onClick={()=> confirm(original.lot_no, original.current_transaction)} 
            /></Tooltip>}

            {original.state === POND_STATUS.PRODUCING &&
            <Tooltip triger='hover' title={"จบการทำงาน"}>
              <ColorButton 
                type='primary'
                override={GRAY_COLOR}
                style={{ marginLeft : 10}}
                icon={<StopFilled />}
                onClick={()=> confirmEnd(original.lot_no, original.current_transaction)} 
            /></Tooltip>}
          </div>
      )}
    },
    {
      title: 'บ่อพัก',
      dataIndex: 'name',
      key: 'name',
      searcher : true,
      render : (value) => value ? value : '-'
    },
    {
      title: 'Lot No.',
      dataIndex: 'lot_no',
      key: 'lot_no',
      render : (value, original) => value ? 
        <>
          {value}
          <Tooltip triger='hover' title='รายละเอียดสต๊อกน้ำยางสด'>
            <Button type='link' icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.POND_SUMMARY}/${original.current_transaction}`)}/>
          </Tooltip>
        </> : '-'
    },
    {
      title: 'ความสูง (cm)',
      dataIndex: 'current_height',
      key: 'current_height',
      render : (value) => <center>{value ? value : '-'}</center>
    },
    {
      title: 'น้ำหนักสะพานชั่ง (Kgs)',
      dataIndex: 'truck_transactions_weight',
      key: 'truck_transactions_weight',
      render : (value) => <center>{value ? formatComma(toFixed(value, 0)) : '-'}</center>
    },
    {
      title: 'น้ำหนักวัดเทียบ (Kgs)',
      dataIndex: 'current_weight',
      key: 'current_weight',
      render : (value, original) => {
        return (
          <center>
            {value ? <Tooltip triger='hover' title={"ประวัติเปลี่ยนน้ำหนัก"}><a onClick={() => setTargetWeightLog(original.current_transaction)}>{formatComma(toFixed(value, 0))}</a></Tooltip> : '-'}
            {_.get(original, 'current_transaction', 'null') && 
              <PondWeightPopover
                title={"น้ำหนักน้ำยางสด"}
                target={original}
                onUpdate={() => refreshData()}
              >
                <Tooltip triger='hover' title={"กรอกน้ำหนัก"}>
                  <EditOutlined style={{ color : colorWarning, marginLeft : 5 }}/>
                </Tooltip>
              </PondWeightPopover>
            }
          </center>)
      }
    },
    {
      title: 'สถานะ',
      dataIndex: 'state',
      key: 'state',
      render : (value, original) => {
        return (
          <>
          {value ? <Tooltip triger='hover' title={"ประวัติเปลี่ยนสถานะ"}><a onClick={() => setTargetStatusLog(original.current_transaction)}>{POND_STATUS_TH[value]}</a></Tooltip> : '-'}
          </>
          )
      }
    },
  ]

  React.useEffect(() => {
    const interval = setInterval(() => {
      refreshData();
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  return(
    <div>
      <ErrorMessages message={errorMessage}/>
      <TSTable
        ref={tableRef}
        columns={columns}
        size={'small'}
        url={URL_POND.POND}
        params={{ state : props.role === 'tank' ? POND_STATUS.PRODUCING : ''}}
        rowClassName={(record) => {
          if (record.is_input_progressing) {
            return 'table-row-red'
          }
        }}
      />

      <PondOpenLotModal
        open={openLotTarget ? true : false}
        onClose={() => {
          setOpenLotTarget(false)
        }}
        target={openLotTarget}
        onUpdate={() => {
          setOpenLotTarget(null)
          refreshData();
        }}
      />

      <PondTruckLogModal
        open={truckLogTarget ? true : false}
        onClose={() => setTruckLogTarget(null)}
        target={truckLogTarget}
        onUpdate={() => {
          setTruckLogTarget(null)
          refreshData();
        }}
      />
    
      <PondCloseModal
        open={closePondTarget ? true : false}
        onClose={() => setClosePondTarget(null)}
        target={closePondTarget}
        onUpdate={() => {
          setClosePondTarget(null)
          refreshData();
        }}
      />

      <PondWeightLogModal
        open={targetWeightLog ? true : false}
        target={targetWeightLog}
        onClose={() => 
          setTargetWeightLog(null)
        }
      />

      <PondStatusLogModal
        open={targetStatusLog ? true : false}
        target={targetStatusLog}
        onClose={() => 
          setTargetStatusLog(null)
        }
        onUpdate={() => {
          setTargetStatusLog(null)
          refreshData();
        }}
      />
    </div>
  )
})

export default PondLatexList;